import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getTables, getSections, getBranches, deleteTable } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import SelectBox from "../components/UI/SelectBox";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function Tables() {
  const { t } = useTranslation("tables");
  const { t: tCommon } = useTranslation("common");
  const [tables, setTables] = useState([]);
  const [sections, setSections] = useState([]);
  const [branches, setBranches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchTables = async () => {
    try {
      const params = {};
      if (branchFilter) params.branch_id = branchFilter;
      if (sectionFilter) params.section_id = sectionFilter;
      const data = await getTables(token, params);
      setTables(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchTables(); }, [branchFilter, sectionFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getSections(token).then(setSections).catch(() => notify.error(t("notify_load_sections_failed", "Failed to load sections"))); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getBranches(token).then(setBranches).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branchOptions = [{ value: "", label: t("filter_branch_all_option", "All branches") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];
  const visibleSections = branchFilter ? sections.filter((s) => String(s.branch_id) === branchFilter) : sections;
  const sectionOptions = [{ value: "", label: t("filter_section_all_option", "All sections") }, ...visibleSections.map((s) => ({ value: String(s.id), label: s.name }))];

  const sortedAndFiltered = useMemo(() => {
    let result = [...tables].filter((tbl) => tbl.name.toLowerCase().includes(searchTerm.toLowerCase()));

    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [tables, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deleteTable(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Table deleted successfully"));
      fetchTables();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_delete_failed", "Failed to delete table"));
    }
  };

  const columns = [
    {
      key: "name",
      label: t("col_name", "Name"),
      render: (row) => (
        <div className="flex items-center gap-3 font-bold">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <ICONS.Tables className="w-4 h-4" />
          </div>
          <div>{row.name}</div>
        </div>
      ),
    },
    { key: "section", label: t("col_section", "Section"), className: "text-gray-600", render: (row) => row.section?.name || `#${row.section_id}` },
    { key: "branch", label: t("col_branch", "Branch"), className: "text-gray-600", render: (row) => row.branch?.name || `#${row.branch_id}` },
    { key: "capacity", label: t("col_capacity", "Capacity"), className: "text-gray-600 font-mono", render: (row) => row.capacity || "—" },
    {
      key: "is_active",
      label: t("col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
          {row.is_active ? tCommon("status_active", "Active") : tCommon("status_inactive", "Inactive")}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Table")}
        message={t("delete_confirm_message", "Are you sure you want to remove this table?")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Tables size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Tables")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Physical tables within each section")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder={t("search_placeholder", "Search by name...")}
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-table")} className="w-full sm:w-auto shrink-0">
            {t("btn_add_table", "Add Table")}
          </Button>
        </div>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SelectBox label={t("filter_branch_label", "Filter by branch")} options={branchOptions} value={branchFilter} onChange={(val) => { setBranchFilter(val); setSectionFilter(""); setCurrentPage(1); }} />
        <SelectBox label={t("filter_section_label", "Filter by section")} options={sectionOptions} value={sectionFilter} onChange={(val) => { setSectionFilter(val); setCurrentPage(1); }} />
      </div>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-table/${row.id}`)}>{isMobile && ` ${tCommon("btn_edit", "Edit")}`}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.section?.name}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
